/* eslint-disable @nx/enforce-module-boundaries */
import { isEqual } from 'lodash';
import { ColumnState } from '@ag-grid-community/core';
import {
  FieldType,
  FilterOperator,
  FilterType,
  FilterValue,
} from '@ellf/common-interfaces';
import { AgGridFilter, AgGridFilterModel } from './aggrid-models';
import { EntityListField } from './entity-list-field';

export function mapToFilterModel(filters: FilterValue[]) {
  const filterModel: AgGridFilterModel = {};
  return filters.reduce((res, f) => {
    if (f.type !== FilterType.date && f.type !== FilterType.datetime) {
      res[f.field] = buildAgGridFilter(f);
    } else {
      res[f.field] = buildAgGridDateFilter(f);
    }

    return res;
  }, filterModel);
}

export function getAgGridFilterType(filterType: FilterType) {
  switch (filterType) {
    case FilterType.date:
    case FilterType.datetime:
      return 'date';
    case FilterType.number:
      return 'number';
    case FilterType.boolean:
      return 'set';
    default:
      return 'text';
  }
}

export function getDefaultOperator(type: FilterType) {
  if (type === FilterType.text) {
    return FilterOperator.contains;
  } else if (type === FilterType.date || type === FilterType.datetime) {
    return FilterOperator.equals;
  } else if (type === FilterType.number) {
    return FilterOperator.equals;
  } else if (type === FilterType.boolean) {
    return FilterOperator.equals;
  } else if (type === FilterType.enum) {
    return FilterOperator.equals;
  } else if (type === FilterType.autocomplete) {
    return FilterOperator.equals;
  }

  return FilterOperator.contains;
}

export function buildAgGridFilter(f: {
  type: FilterType;
  operator: FilterOperator;
  value: any[];
}): AgGridFilter {
  const obj = {
    type: FilterOperator[f.operator],
    filterType: getAgGridFilterType(f.type),
    filter: f.value.length ? f.value[0] : undefined,
    filterTo: f.value.length === 2 ? f.value[1] : undefined,
  } as AgGridFilter;

  if (obj.filterTo == null) {
    delete obj.filterTo;
  }
  return obj;
}
export function buildAgGridDateFilter(f: {
  type: FilterType;
  operator: FilterOperator;
  value: any[];
}): AgGridFilter {
  const obj = {
    type: FilterOperator[f.operator],
    filterType: getAgGridFilterType(f.type),
    dateFrom: f.value.length ? f.value[0] : undefined,
    dateTo: f.value.length === 2 ? f.value[1] : undefined,
  } as AgGridFilter;

  if (obj.dateTo == null) {
    obj.dateTo = null;
  }
  return obj;
}

export function mapFromFilterModel(
  filterModel?: AgGridFilterModel,
  fields?: EntityListField[],
) {
  if (!filterModel) {
    return [];
  }
  const filters: FilterValue[] = [];
  Object.keys(filterModel).forEach((key) => {
    const gridFilter = filterModel[key];
    const f = {
      field: key,
      operator: (<any>FilterOperator)[gridFilter.type],
      type:
        fields?.find((f) => f.field === key)?.filterType ??
        (<any>FilterType)[gridFilter.filterType],
      value: [],
    } as FilterValue;

    if (f.type !== FilterType.date && f.type !== FilterType.datetime) {
      if (gridFilter.filter != null) {
        f.value.push(gridFilter.filter);
      }

      if (gridFilter.filterTo != null) {
        f.value.push(gridFilter.filterTo);
      }
    } else {
      if (gridFilter.dateFrom != null) {
        f.value.push(gridFilter.dateFrom);
      }

      if (gridFilter.dateTo != null) {
        f.value.push(gridFilter.dateTo);
      }
    }

    filters.push(f);
  });
  return filters;
}

export function mapFromColumnState(columnState?: ColumnState[]) {
  if (!columnState) return undefined;
  return columnState
    .filter((cs) => !!cs.sort && !!cs.colId)
    .reduce((acc: { [key: string]: any }, item) => {
      const colId = item.colId as string;
      const sort = item.sort as string;
      return { ...acc, [colId]: sort.toUpperCase() };
    }, {});
}

export function equalFilters(
  gridFilterModel: AgGridFilterModel | undefined,
  filters: FilterValue[],
) {
  const filtersMapped = mapToFilterModel(filters);

  if (!isEqual(filtersMapped, gridFilterModel)) {
    return false;
  }
  return true;
}

export function getGridColumnType(type: FilterType, fieldType?: FieldType) {
  switch (type) {
    case FilterType.datetime:
      return 'datetimeColumn';
    case FilterType.date:
      return 'dateColumn';
    case FilterType.number:
      if (fieldType === FieldType.currency) return 'currencyColumn';
      else return 'numberColumn';
    case FilterType.boolean:
      return 'boolColumn';
    case FilterType.enum:
      return 'enumColumn';
    default:
      return ''; // text dà errore in console
  }
}

export function getEventPath(event: Event): HTMLElement[] {
  const path: HTMLElement[] = [];
  let currentTarget: any = event.target;
  while (currentTarget) {
    path.push(currentTarget);
    currentTarget = currentTarget.parentElement;
  }
  return path;
}

export function getAllFocusableElementsOf(el: HTMLElement) {
  return Array.from<HTMLElement>(
    el.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((focusableEl) => {
    return focusableEl.tabIndex !== -1;
  });
}
